import { useState, useRef } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUpload = ({ value, onChange, label = "Image" }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError("");
    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = localStorage.getItem("admin_token");
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      // Build full URL for the uploaded image
      onChange(`${BACKEND_URL}${data.url}`);
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <label className="block text-xs font-medium text-neutral-500 font-['Manrope'] mb-2 uppercase tracking-wider">
        {label}
      </label>

      {/* Preview */}
      {value && (
        <div className="relative mb-3 rounded-xl overflow-hidden border border-white/[0.06] h-32 group">
          <img src={value} alt="Preview" className="w-full h-full object-cover opacity-80" />
          <button
            onClick={() => onChange("")}
            className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/60 text-neutral-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Upload area */}
      <div className="flex gap-2">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Image URL or upload a file..."
          className="flex-1 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm font-['Manrope']
            placeholder:text-neutral-600
            transition-colors duration-300
            focus:outline-none focus:border-white/[0.2] focus:bg-white/[0.06]"
        />
        <button
          data-testid="image-upload-button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/[0.06] border border-white/[0.08] text-sm text-neutral-400 font-['Manrope']
            transition-colors duration-200
            hover:border-white/[0.16] hover:text-white
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? (
            <div className="w-4 h-4 border-2 border-neutral-600 border-t-white rounded-full animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          {uploading ? "..." : "Upload"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
          onChange={handleUpload}
          className="hidden"
        />
      </div>

      {error && (
        <p className="mt-2 text-xs text-red-400 font-['Manrope']">{error}</p>
      )}
    </div>
  );
};

export default ImageUpload;
